import React, { useContext, useEffect } from "react";
import {
  GridContainer,
  BillingInfoFormRootArea,
  GridItem,
} from "./BillingInfoForm.styles";
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  TextField as MuiTextField,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import AuthContext from "../../../context/AuthContext";
import { useEffectOnce } from "../../../utils/effects";
import {
  getBillingInfoById,
  createBillingInfo,
  updateBillingInfo,
} from "./BillingInfoForm.thunk";
import {
  billingInfoInitialValues,
  billingInfoInitialValidationSchema,
  setBillingInfoValues,
} from "./BillingInfoForm.resources";
import TextField from "../../textField/TextField";
import CountrySelectField from "../../selectField/CountrySelectField";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const BillingInfoForm = (props) => {
  const {
    setSubmenuMerchantTerminal,
    setSelectedOption,
    setNameSection,
    goBack,
  } = props;

  const dispatch = useDispatch();
  const { token, acquirerData } = useContext(AuthContext);

  const {
    billingInfoData,
    billingInfoId,
    statusRetrieve,
    statusCreate,
    statusUpdate,
    errorRetrieve,
  } = useSelector((state) => state.billingInfoForm);

  const modeForm = billingInfoId ? "update" : "create";
  const isLoading =
    statusRetrieve === "loading" ||
    statusCreate === "loading" ||
    statusUpdate === "loading";

  useEffectOnce(() => {
    if (billingInfoId) {
      dispatch(getBillingInfoById({ token, billingInfoId }));
    }
  });

  // Effect to set form values when billingInfoData changes (for editing)
  useEffect(() => {
    if (billingInfoData && billingInfoId) {
      setBillingInfoValues({ billingInfoData, setFieldValue });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingInfoData, billingInfoId]);

  const handleGoBack = () => {
    if (goBack) {
      goBack();
    } else {
      setSubmenuMerchantTerminal("billing_info");
      setSelectedOption("billing_info");
      setNameSection("Merchant & Terminal - Customer Info");
    }
  };

  const {
    handleChange,
    handleBlur,
    handleSubmit,
    values,
    errors,
    touched,
    setSubmitting,
    setFieldValue,
    isSubmitting,
  } = useFormik({
    initialValues: billingInfoInitialValues,
    validationSchema: billingInfoInitialValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setSubmitting(true);
      try {
        // Include merchant_id from context (not shown in UI)
        const formData = {
          ...values,
          merchant_id: acquirerData?.merchantID || "",
        };

        if (modeForm === "create") {
          await dispatch(
            createBillingInfo({ token, handleGoBack, ...formData })
          ).unwrap();
        } else {
          await dispatch(
            updateBillingInfo({
              token,
              handleGoBack,
              billingInfoId,
              ...formData,
            })
          ).unwrap();
        }
      } catch (error) {
        console.error("Error submitting form:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (statusRetrieve === "loading") {
    return (
      <BillingInfoFormRootArea>
        <GridContainer>
          <GridItem xs={12}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="400px"
              width="100%"
            >
              <CircularProgress />
            </Box>
          </GridItem>
        </GridContainer>
      </BillingInfoFormRootArea>
    );
  }

  if (statusRetrieve === "failed" && errorRetrieve) {
    return (
      <BillingInfoFormRootArea>
        <Box sx={{ p: 3 }}>
          <Typography color="error" variant="h6" gutterBottom>
            Error loading billing info
          </Typography>
          <Typography color="error">{errorRetrieve}</Typography>
          <Button
            variant="outlined"
            onClick={handleGoBack}
            startIcon={<ArrowBackIcon />}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </BillingInfoFormRootArea>
    );
  }

  return (
    <BillingInfoFormRootArea>
      <GridContainer>
        <GridContainer
          justifyContent="space-between"
          alignItems="center"
          padding="15px 0 0"
        >
          <GridItem xs={10}>
            <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
              {modeForm === "create"
                ? "Create Customer Info"
                : "Edit Customer Info"}
            </Typography>
          </GridItem>
          <GridItem xs={2}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleGoBack}
              size="small"
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
          </GridItem>
        </GridContainer>

        <GridItem xs={12}>
          <form onSubmit={handleSubmit}>
            <GridContainer spacing={2}>
              <GridItem xs={12} md={6} padding="15px 15px 15px 0">
                <TextField
                  id="company_name"
                  name="company_name"
                  fullWidth
                  label="Company Name"
                  value={values.company_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.company_name}
                  touched={touched.company_name}
                />
              </GridItem>

              <GridItem xs={12} md={6} padding="15px 15px 15px 0">
                <TextField
                  id="internal_company_number"
                  name="internal_company_number"
                  fullWidth
                  label="Internal Company Number"
                  value={values.internal_company_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.internal_company_number}
                  touched={touched.internal_company_number}
                />
              </GridItem>

              <GridItem xs={12} md={6} padding="15px 15px 15px 0">
                <TextField
                  id="first_name"
                  name="first_name"
                  fullWidth
                  label="First Name"
                  value={values.first_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={errors.first_name}
                  touched={touched.first_name}
                />
              </GridItem>

              <GridItem xs={12} md={6} padding="15px 15px 15px 0">
                <TextField
                  id="last_name"
                  name="last_name"
                  fullWidth
                  label="Last Name"
                  value={values.last_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={errors.last_name}
                  touched={touched.last_name}
                />
              </GridItem>

              <GridItem xs={12} md={6} padding="15px 15px 15px 0">
                <MuiTextField
                  id="email"
                  name="email"
                  fullWidth
                  label="Email"
                  type="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  disabled={modeForm === "update"}
                  error={Boolean(touched.email && errors.email)}
                  helperText={
                    modeForm === "update"
                      ? "Email cannot be changed when editing"
                      : touched.email && errors.email
                      ? errors.email
                      : ""
                  }
                />
              </GridItem>

              <GridItem xs={12} md={6} padding="15px 15px 15px 0">
                <TextField
                  id="address1"
                  name="address1"
                  fullWidth
                  label="Address Line 1"
                  value={values.address1}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={errors.address1}
                  touched={touched.address1}
                />
              </GridItem>

              <GridItem xs={12} md={6} padding="15px 15px 15px 0">
                <TextField
                  id="address2"
                  name="address2"
                  fullWidth
                  label="Address Line 2"
                  value={values.address2}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.address2}
                  touched={touched.address2}
                />
              </GridItem>

              <GridItem xs={12} md={6} padding="15px 15px 15px 0">
                <TextField
                  id="city"
                  name="city"
                  fullWidth
                  label="City"
                  value={values.city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={errors.city}
                  touched={touched.city}
                />
              </GridItem>

              <GridItem xs={12} md={6} padding="15px 15px 15px 0">
                <TextField
                  id="state"
                  name="state"
                  fullWidth
                  label="State"
                  value={values.state}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  maxLength={30}
                  error={errors.state}
                  touched={touched.state}
                  helperText="State or province"
                />
              </GridItem>

              <GridItem xs={12} md={6} padding="15px 15px 15px 0">
                <CountrySelectField
                  id="country"
                  name="country"
                  fullWidth
                  label="Country"
                  value={values.country}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.country}
                  touched={touched.country}
                />
              </GridItem>

              <GridItem xs={12} md={6} padding="15px 15px 15px 0">
                <TextField
                  id="postal_code"
                  name="postal_code"
                  fullWidth
                  label="Postal Code"
                  value={values.postal_code}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  maxLength={20}
                  error={errors.postal_code}
                  touched={touched.postal_code}
                  helperText="Postal Code"
                />
              </GridItem>

              <GridItem xs={12} md={6} padding="15px 15px 15px 0">
                <TextField
                  id="phone"
                  name="phone"
                  fullWidth
                  label="Phone"
                  value={values.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.phone}
                  touched={touched.phone}
                />
              </GridItem>

              <GridItem xs={12} padding="0 15px 35px 0">
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                    mt: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleGoBack}
                    disabled={isLoading || isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isLoading || isSubmitting}
                  >
                    {isLoading || isSubmitting
                      ? "Saving..."
                      : modeForm === "create"
                      ? "Create"
                      : "Update"}
                  </Button>
                </Box>
              </GridItem>
            </GridContainer>
          </form>
        </GridItem>
      </GridContainer>
    </BillingInfoFormRootArea>
  );
};

export default BillingInfoForm;
